/**
 * QueueDepthPanel — Dev-only diagnostic panel showing offline sync queue state.
 *
 * Visibility:
 *   - URL param ?debug=queue -> activates, persists in localStorage
 *   - URL param ?debug=off -> deactivates
 *   - localStorage 'aif_debug_queue' === '1' -> active
 *   - Hot-key Ctrl+Shift+Q -> toggle active state
 *   - Close button (X) -> deactivate
 *
 * Visual: Fixed bottom-right, dark theme, small.
 */
import { useEffect, useState, useCallback } from 'react';
import { useQueueStatus } from '../../hooks/useQueueStatus';

const STORAGE_KEY = 'aif_debug_queue';

function readActiveFromStorage() {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeActiveToStorage(value) {
  try {
    localStorage.setItem(STORAGE_KEY, value ? '1' : '0');
  } catch { /* ignore */ }
}

function processUrlParam() {
  if (typeof window === 'undefined') return readActiveFromStorage();
  const params = new URLSearchParams(window.location.search);
  const debugParam = params.get('debug');
  if (debugParam === 'queue') {
    writeActiveToStorage(true);
    return true;
  }
  if (debugParam === 'off') {
    writeActiveToStorage(false);
    return false;
  }
  return readActiveFromStorage();
}

const STATUS_COLORS = {
  idle: '#10b981',
  syncing: '#3b82f6',
  stuck: '#f59e0b',
  offline: '#ef4444',
};

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatRelative(timestamp) {
  if (!timestamp) return 'never';
  const diff = Date.now() - timestamp;
  if (diff < 1000) return 'just now';
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  return `${Math.floor(diff / 3_600_000)}h ago`;
}

export default function QueueDepthPanel() {
  const [active, setActive] = useState(() => processUrlParam());
  const status = useQueueStatus();

  useEffect(() => {
    const onKeydown = (e) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'Q' || e.key === 'q')) {
        e.preventDefault();
        setActive(prev => {
          const next = !prev;
          writeActiveToStorage(next);
          return next;
        });
      }
    };
    window.addEventListener('keydown', onKeydown);
    return () => window.removeEventListener('keydown', onKeydown);
  }, []);

  const close = useCallback(() => {
    writeActiveToStorage(false);
    setActive(false);
  }, []);

  if (!active) return null;

  const statusColor = STATUS_COLORS[status.syncStatus] || '#6b7280';

  return (
    <div
      data-testid="queue-depth-panel"
      style={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 'var(--aif-z-critical)',
        background: 'rgba(17, 24, 39, 0.95)',
        color: '#e5e7eb',
        padding: '12px 14px',
        borderRadius: 8,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 11,
        lineHeight: 1.5,
        minWidth: 240,
        boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
        border: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <span style={{ fontWeight: 600, color: '#fff' }}>Queue Status</span>
        <button
          onClick={close}
          aria-label="Close debug panel"
          style={{ background: 'transparent', border: 0, color: '#9ca3af', cursor: 'pointer', padding: 0, fontSize: 14 }}
        >
          &times;
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor, display: 'inline-block' }} />
        <span data-testid="status">{status.syncStatus}</span>
      </div>

      <div>Depth: <span data-testid="depth" style={{ color: '#fff' }}>{status.depth}</span></div>
      <div>Last sync: <span data-testid="last-sync">{formatRelative(status.lastSyncTime)}</span></div>
      <div>Tier 1 / Tier 3: <span data-testid="tier-breakdown">{status.tierBreakdown.tier1} / {status.tierBreakdown.tier3}</span></div>
      <div>Storage: <span data-testid="storage">{formatBytes(status.storageUsage)} / {formatBytes(status.storageQuota)} ({status.storagePct}%)</span></div>

      <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.1)', fontSize: 10, color: '#6b7280' }}>
        Ctrl+Shift+Q to toggle &middot; ?debug=off to disable
      </div>
    </div>
  );
}
